import React from 'react';
import { FileText, Target, Coins, Rocket, Shield, Users, TrendingUp } from 'lucide-react';
import { Helmet } from 'react-helmet';

const Whitepaper = () => {
  return (
    <div className="min-h-screen bg-black text-white pt-32 pb-20">
      <Helmet>
        <link rel="canonical" href="https://www.flumberico.fun/whitepaper" />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <FileText className="w-20 h-20 text-gold mx-auto mb-6" />
          <h1 className="text-6xl font-black mb-6 text-gold">
            FLUMBERICO
          </h1>
          <p className="text-2xl text-silver font-semibold">Official Whitepaper</p>
          <p className="text-lg text-silver/70 mt-2">The SEO Social Experiment Meme Coin</p>
        </div>

        {/* Executive Summary */}
        <section className="mb-12 bg-gradient-to-br from-gold/10 to-transparent border border-gold/30 rounded-lg p-8">
          <h2 className="text-3xl font-bold text-gold mb-6 flex items-center">
            <Target className="mr-3" /> Executive Summary
          </h2>
          <p className="text-white text-lg leading-relaxed">
            <span className="font-bold text-gold">FLUMBERICO ($FLUMBERICO)</span> <span className="text-white">represents a groundbreaking convergence of search engine optimization, social experimentation, and cryptocurrency tokenomics. This project challenges SEO professionals and enthusiasts worldwide to compete for the #1 ranking position for the keyword "flumberico" on Google, rewarding participants with $FLUMBERICO tokens based on their link-building effectiveness and domain authority.</span>
          </p>
        </section>

        {/* The Concept */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gold mb-6">The Concept</h2>
          <div className="bg-black/50 border border-silver/30 rounded-lg p-8">
            <h3 className="text-2xl font-semibold text-silver mb-4">What is the FLUMBERICO Experiment?</h3>
            <p className="text-white mb-4 leading-relaxed">
              FLUMBERICO is an ultra-fast trending SEO social experiment where search engine optimization professionals, digital marketers, content creators, and crypto enthusiasts compete to achieve the coveted #1 ranking for the term "flumberico" on major search engines, primarily Google.
            </p>
            <p className="text-white mb-4 leading-relaxed">
              The experiment tests modern SEO strategies including:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li><a href="/blog/high-authority-backlinks" className="text-gold hover:underline">High-authority backlink acquisition</a></li>
              <li><a href="/blog/domain-authority-explained" className="text-gold hover:underline">Domain authority optimization</a></li>
              <li>Social signal amplification</li>
              <li><a href="/blog/guest-posting-backlinks" className="text-gold hover:underline">Content marketing and guest posting</a></li>
              <li>Technical SEO implementation</li>
              <li><a href="/blog/white-hat-link-building" className="text-gold hover:underline">Community-driven white hat link building</a></li>
            </ul>
          </div>
        </section>

        {/* Tokenomics */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gold mb-6 flex items-center">
            <Coins className="mr-3" /> Tokenomics
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-gold/20 to-black/50 border border-gold/40 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gold mb-3">Token Details</h3>
              <ul className="text-white space-y-2">
                <li><span className="text-gold font-semibold">Name:</span> FLUMBERICO</li>
                <li><span className="text-gold font-semibold">Symbol:</span> $FLUMBERICO</li>
                <li><span className="text-gold font-semibold">Blockchain:</span> Solana</li>
                <li><span className="text-gold font-semibold">Type:</span> Meme Coin / Utility Token</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-silver/20 to-black/50 border border-silver/40 rounded-lg p-6">
              <h3 className="text-xl font-bold text-silver mb-3">Distribution Model</h3>
              <ul className="text-white space-y-2">
                <li><span className="text-silver font-semibold">Link Rewards:</span> Based on DA & Link Juice</li>
                <li><span className="text-silver font-semibold">Phase 1:</span> Manual Distribution</li>
                <li><span className="text-silver font-semibold">Phase 2:</span> Automated Smart Contracts</li>
                <li><span className="text-silver font-semibold">Verification:</span> Developer-Audited</li>
              </ul>
            </div>
          </div>
          
          {/* Fund Allocation */}
          <div className="mt-8 bg-gradient-to-br from-gold/30 via-gold/10 to-black/50 border-2 border-gold/50 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-gold mb-4 flex items-center">
              <TrendingUp className="mr-3" /> Fund Allocation & Transparency
            </h3>
            <div className="bg-black/40 rounded-lg p-6 mb-4">
              <p className="text-white text-lg leading-relaxed mb-4">
                <span className="text-gold font-bold text-2xl">50%</span> <span className="text-white">of all funds raised from buying</span> <span className="text-gold font-bold">$FLUMBERICO</span> <span className="text-white">will be allocated directly to</span> <span className="text-gold font-bold">link-building initiatives</span><span className="text-white">.</span>
              </p>
              <p className="text-white text-lg leading-relaxed mb-4">
                These link-building campaigns will actively promote the "flumberico" keyword across high-authority domains, contributing to the SEO competition and creating real value for the ecosystem.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-black/30 border border-gold/30 rounded-lg p-4">
                <h4 className="text-gold font-bold text-lg mb-2">📊 Trackable on Ahrefs</h4>
                <p className="text-white text-sm">
                  All acquired backlinks will be indexed and visible on Ahrefs, showing domain ratings, referring domains, and link equity growth in real-time.
                </p>
              </div>
              
              <div className="bg-black/30 border border-gold/30 rounded-lg p-4">
                <h4 className="text-gold font-bold text-lg mb-2">📈 Trackable on SEMrush</h4>
                <p className="text-white text-sm">
                  Monitor keyword rankings, backlink profiles, and domain authority improvements through SEMrush as links get indexed and contribute to SEO power.
                </p>
              </div>
            </div>
            
            <div className="mt-4 bg-gold/10 border border-gold/30 rounded-lg p-4">
              <p className="text-white text-sm">
                <span className="text-gold font-semibold">Full Transparency:</span> The community can verify every link acquisition and track the growth of the flumberico brand's SEO equity using industry-standard tools. This creates a transparent, verifiable connection between token purchases and real-world SEO value creation.
              </p>
            </div>
          </div>
          
          {/* Value Appreciation Mechanism */}
          <div className="mt-8 bg-gradient-to-br from-silver/30 via-silver/10 to-black/50 border-2 border-silver/50 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-silver mb-4 flex items-center">
              <Rocket className="mr-3" /> Token Value Appreciation Mechanism
            </h3>
            
            <div className="bg-black/40 rounded-lg p-6 mb-4">
              <h4 className="text-gold font-bold text-xl mb-3">The Value Growth Cycle</h4>
              <p className="text-white text-lg leading-relaxed mb-4">
                As the <span className="text-gold font-bold">flumberico.fun</span> domain value rises through accumulated backlinks and social mentions, the inherent value of links and guest posts on the site increases proportionally. This creates a virtuous cycle of value creation.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gold/10 border border-gold/30 rounded-lg p-5 text-center">
                <div className="text-4xl mb-2">🔗</div>
                <h5 className="text-gold font-bold mb-2">Step 1: Domain Growth</h5>
                <p className="text-white text-sm">
                  Backlinks & social mentions increase flumberico.fun's domain authority and SEO value
                </p>
              </div>
              
              <div className="bg-gold/10 border border-gold/30 rounded-lg p-5 text-center">
                <div className="text-4xl mb-2">💰</div>
                <h5 className="text-gold font-bold mb-2">Step 2: Revenue Generation</h5>
                <p className="text-white text-sm">
                  Higher domain value = premium prices for links and guest posts on the site
                </p>
              </div>
              
              <div className="bg-gold/10 border border-gold/30 rounded-lg p-5 text-center">
                <div className="text-4xl mb-2">🔥</div>
                <h5 className="text-gold font-bold mb-2">Step 3: Token Burn</h5>
                <p className="text-white text-sm">
                  50% of revenue from selling links/posts used to burn $FLUMBERICO tokens
                </p>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-gold/20 to-silver/20 border border-gold/40 rounded-lg p-6">
              <h4 className="text-gold font-bold text-xl mb-3">🔥 Deflationary Token Burn Strategy</h4>
              <p className="text-white text-lg leading-relaxed mb-4">
                The developer will monetize the flumberico.fun domain by selling links and guest post placements to SEO professionals and businesses. <span className="text-gold font-bold text-xl">50%</span> of all funds raised from these sales will be used to <span className="text-gold font-bold">burn $FLUMBERICO tokens</span>, permanently removing them from circulation.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-black/40 rounded-lg p-4">
                  <p className="text-white text-sm mb-2">
                    <span className="text-gold font-semibold">Decreasing Supply:</span> Token burns reduce total supply, creating scarcity
                  </p>
                </div>
                <div className="bg-black/40 rounded-lg p-4">
                  <p className="text-white text-sm mb-2">
                    <span className="text-silver font-semibold">Increasing Value:</span> Lower supply with sustained demand drives token price appreciation
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-4 bg-black/30 border border-silver/30 rounded-lg p-5">
              <p className="text-white leading-relaxed">
                <span className="text-silver font-semibold">Self-Sustaining Ecosystem:</span> This creates a self-reinforcing cycle where SEO competition increases domain value, which generates revenue, which burns tokens, which increases token value, which attracts more participants to the SEO competition. Every stakeholder benefits from the ecosystem's growth.
              </p>
            </div>
          </div>
        </section>

        {/* How to Earn */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gold mb-6">Earning $FLUMBERICO Tokens</h2>
          <div className="bg-black/50 border border-gold/30 rounded-lg p-8">
            <h3 className="text-2xl font-semibold text-gold mb-6">Ranking is Banking!</h3>
            <p className="text-white mb-6 leading-relaxed">
              Participants earn $FLUMBERICO tokens by creating high-quality backlinks to <span className="text-gold font-semibold">flumberico.fun</span>. The reward amount is calculated based on:
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-4 bg-gold/10 rounded-lg p-4">
                <div className="font-black rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FFD700', color: '#000000' }}>1</div>
                <div>
                  <h4 className="text-gold font-bold mb-1">Domain Authority (DA)</h4>
                  <p className="text-white text-sm">Higher DA domains yield more tokens. Links from DA 50+ sites receive premium rewards.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 bg-silver/10 rounded-lg p-4">
                <div className="font-black rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#C0C0C0', color: '#000000' }}>2</div>
                <div>
                  <h4 className="text-silver font-bold mb-1">Link Juice Quality</h4>
                  <p className="text-white text-sm">Follow links from relevant, topically-aligned content provide maximum link equity and rewards.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 bg-gold/10 rounded-lg p-4">
                <div className="font-black rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FFD700', color: '#000000' }}>3</div>
                <div>
                  <h4 className="text-gold font-bold mb-1">Content Quality</h4>
                  <p className="text-white text-sm">Well-written, contextually relevant content with natural anchor text receives bonus multipliers.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 bg-silver/10 rounded-lg p-4">
                <div className="font-black rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#C0C0C0', color: '#000000' }}>4</div>
                <div>
                  <h4 className="text-silver font-bold mb-1">Social Signals</h4>
                  <p className="text-white text-sm">Viral content with high engagement (shares, likes, comments) amplifies token rewards.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Roadmap */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gold mb-6 flex items-center">
            <Rocket className="mr-3" /> Roadmap
          </h2>
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-gold/20 to-transparent border-l-4 border-gold rounded-lg p-6">
              <h3 className="text-xl font-bold text-gold mb-2">Phase 1: Launch & Manual Distribution (Current)</h3>
              <ul className="text-white space-y-1 ml-4 list-disc list-inside">
                <li>Website launch and brand establishment</li>
                <li>Initial token distribution on Pump.fun</li>
                <li>Manual verification and reward distribution via <a href="https://t.me/flumberico" target="_blank" rel="noopener noreferrer" class="text-gold hover:underline font-semibold">official Telegram ↗</a></li>
                <li>Community building and early adopter outreach</li>
                <li>SEO competition kickoff</li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-r from-silver/20 to-transparent border-l-4 border-silver rounded-lg p-6">
              <h3 className="text-xl font-bold text-silver mb-2">Phase 2: Automation & Scale</h3>
              <ul className="text-white space-y-1 ml-4 list-disc list-inside">
                <li>Smart contract deployment for automated distribution</li>
                <li>Link verification API integration</li>
                <li>Real-time dashboard for ranking tracking</li>
                <li>Expanded social media presence</li>
                <li>Partnership with SEO tools and platforms</li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-r from-gold/20 to-transparent border-l-4 border-gold rounded-lg p-6">
              <h3 className="text-xl font-bold text-gold mb-2">Phase 3: Ecosystem Expansion</h3>
              <ul className="text-white space-y-1 ml-4 list-disc list-inside">
                <li>Additional keyword competitions</li>
                <li>Token utility expansion (staking, governance)</li>
                <li>Educational content and SEO courses</li>
                <li>Major exchange listings</li>
                <li>Brand equity monetization strategies</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Community */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gold mb-6 flex items-center">
            <Users className="mr-3" /> Community & Governance
          </h2>
          <div className="bg-black/50 border border-gold/30 rounded-lg p-8">
            <p className="text-white mb-4 leading-relaxed">
              The FLUMBERICO community is central to the project's success. As the experiment grows, token holders will participate in governance decisions including:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li>New keyword competition launches</li>
              <li>Reward structure modifications</li>
              <li>Platform feature development priorities</li>
              <li>Partnership and collaboration approvals</li>
              <li>Treasury allocation and utilization</li>
            </ul>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gold mb-6 flex items-center">
            <Shield className="mr-3" /> Risk Disclosure
          </h2>
          <div className="bg-red-950/30 border border-red-500/40 rounded-lg p-8">
            <p className="text-white mb-4 leading-relaxed">
              <span className="font-bold text-red-400">IMPORTANT:</span> $FLUMBERICO is an experimental meme coin and should be treated as a high-risk investment. This whitepaper does not constitute financial advice. Key risks include:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li>Extreme volatility typical of meme coins</li>
              <li>Experimental nature of the SEO reward mechanism</li>
              <li>No guaranteed returns or token value</li>
              <li>Regulatory uncertainties in cryptocurrency markets</li>
              <li>Smart contract and technical risks</li>
            </ul>
            <p className="text-white mt-4 leading-relaxed">
              Only invest what you can afford to lose. Always conduct your own research (DYOR) before participating.
            </p>
          </div>
        </section>

        {/* Conclusion */}
        <section className="text-center">
          <div className="bg-gradient-to-br from-gold/20 via-silver/10 to-gold/20 border border-gold/40 rounded-lg p-10">
            <h2 className="text-4xl font-black mb-6 text-gold">
              Join the Revolution
            </h2>
            <p className="text-xl text-white mb-6">
              FLUMBERICO merges the worlds of SEO, social experimentation, and cryptocurrency in an unprecedented way. Be part of the movement where <span className="text-gold font-bold">ranking truly is banking</span>.
            </p>
            <p className="text-lg text-silver/70">
              For more information, join our community and start building links to flumberico.fun today!
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Whitepaper;